import React from "react";
import PropTypes from "prop-types";
import Modal from "react-modal";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardFooter,
  Form,
  FormGroup,
  FormFeedback,
  Label,
  Input,
  Col,
} from "reactstrap";
import { css } from "react-emotion";
import { Formik } from "formik";
import UploadButton from "../KeyRecoveryRequest/UploadButton";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { dateFormatter } from "@/util/formatter";
import { getBase64 } from "@/util/fileToBase64";
import verificationApi from "@/api/verification";
import swal from "sweetalert2";
import { handleError } from "@/util/errorHandler";
import { MAX_FILE_SIZE_IN_BYTES } from "@/store/constants";

const modalStyles = css`
  position: absolute;
  top: 50%;
  left: 50%;
  right: auto;
  bottom: auto;
  overflow: visible;
  margin-right: -50%;
  transform: translate(-50%, -50%);
  width: 35rem;

  .buttons {
    display: flex;
    justify-content: space-between;
    margin: 15px 0 0;
  }
`;

const overlayStyles = css`
  position: fixed;
  top: 0px;
  left: 0px;
  right: 0px;
  bottom: 0px;
  background-color: rgba(255, 255, 255, 0.75);
  z-index: 1021; /* above navs, below sweetalert */
`;

const datePickerWrapper = css`
  width: 100%;
`;

class UpdateAccountModal extends React.Component {
  state = {
    userPhotoFile: null,
    idPhotoFile: null,
    birthday: null,
  };

  baseState = {
    ...this.state,
  };

  validateFormik = ({
    documentId,
    dateOfBirth: birthday,
    nationality,
    email,
    address,
    sex,
  }) => {
    const errors = {};

    if (!nationality) {
      errors.nationality = "Please enter user's nationality";
    }
    if (
      nationality &&
      nationality.length &&
      !/^[a-zA-Z0-9]+$/g.test(nationality)
    ) {
      errors.nationality =
        "Incorrect nationality. The nationality should contain [a-zA-Z0-9] symbols";
    }

    if (
      email &&
      email.length &&
      !/^(([^<>()\\[\]\\.,;:\s@"]+(\.[^<>()\\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
        email,
      )
    ) {
      errors.email = "Incorrect email. Please provide correct user email";
    }

    if (!sex) {
      errors.sex = "Please enter user's gender";
    }

    if (!birthday) {
      errors.birthday = "Please enter user's birthday";
    }

    if (!documentId) {
      errors.documentId = "Please enter user's document id";
    }
    if (
      documentId &&
      documentId.length &&
      !/^[a-zA-Z0-9]+$/g.test(documentId)
    ) {
      errors.documentId =
        "Incorrect document ID. The document ID should contain [a-zA-Z_0-9] symbols";
    }

    return errors;
  };

  onSelectUserPhoto = file => {
    this.setState({ userPhotoFile: file });
  };

  onSelectUserID = file => {
    this.setState({ idPhotoFile: file });
  };

  onSelectDate = date => {
    const currentYear = new Date().getFullYear();
    const year =
      new Date(date).getFullYear() > currentYear
        ? currentYear
        : new Date(date).getFullYear();
    const newDate = new Date(date).setFullYear(year);

    this.setState({
      birthday: newDate,
    });
  };

  prepareDataBeforeSubmit = async form => {
    const _formatDate = date => {
      if (!date) return;
      const _date = new Date(date);
      const _checkLen = v => (v > 9 ? v : `0${v}`);
      const mm = _checkLen(_date.getMonth() + 1);
      const dd = _checkLen(_date.getDate());
      return `${_date.getFullYear()}-${mm}-${dd} 00:00:00`;
    };

    const {
      birthday: dateOfBirth,
      userPhotoFile: photo,
      idPhotoFile: identity,
    } = this.state;

    const { accountId } = this.props.data;

    return {
      accountId,
      ...form,
      secondaryContact: form.email,
      dateOfBirth: _formatDate(dateOfBirth),
      photo: photo ? await getBase64(photo) : undefined,
      identity: identity ? await getBase64(identity) : undefined,
    };
  };

  onUpdateAccount = async form => {
    const data = await this.prepareDataBeforeSubmit(form);
    try {
      await verificationApi.updateUserAccount(data);
      swal(
        "Success",
        `Information of ${data.fullName} has been changed successfully`,
        "success",
      );
    } catch (error) {
      handleError(error);
    }
  };

  reset = () => {
    this.setState(this.baseState);
  };

  beforeClose = () => {
    this.reset();
    this.props.onModalClose();
  };

  render() {
    const {
      accountId,
      documentId,
      birthday,
      nationality,
      email,
      address,
      sex,
      fullName,
    } = this.props.data;

    const form = (
      <Formik
        initialValues={{
          accountId,
          documentId,
          dateOfBirth: birthday,
          nationality,
          email,
          address,
          sex,
          fullName,
        }}
        validate={this.validateFormik}
        onChange={this.validateFormik}
        onSubmit={this.onUpdateAccount}>
        {({
          values,
          handleSubmit,
          handleChange,
          handleBlur,
          errors,
          setErrors,
        }) => (
          <Form onSubmit={handleSubmit}>
            <CardBody>
              <FormGroup row>
                <Label sm={3}>User account ID</Label>
                <Col sm={9}>
                  <Input
                    type="text"
                    name="accountId"
                    placeholder="Enter user's account id"
                    invalid={Boolean(errors.accountId)}
                    value={values.accountId}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled
                  />
                  {errors.accountId && (
                    <FormFeedback className="text-alert">
                      {errors.accountId}
                    </FormFeedback>
                  )}
                </Col>
              </FormGroup>
              <FormGroup row>
                <Label sm={3}>Full name</Label>
                <Col sm={9}>
                  <Input
                    type="text"
                    name="fullName"
                    placeholder="Enter user's full name"
                    invalid={Boolean(errors.fullName)}
                    value={values.fullName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled
                  />
                  {errors.fullName && (
                    <FormFeedback className="text-alert">
                      {errors.fullName}
                    </FormFeedback>
                  )}
                </Col>
              </FormGroup>
              <FormGroup row>
                <Label sm={3}>User photo</Label>
                <Col sm={9}>
                  <UploadButton
                    onSelectFile={userPhotoFile => {
                      setErrors({ ...errors, userPhotoFile: undefined });
                      this.onSelectUserPhoto(userPhotoFile);
                    }}
                    accept="image/png,image/jpeg"
                    maxFileSize={MAX_FILE_SIZE_IN_BYTES}
                  />
                  {errors.userPhotoFile && (
                    <FormFeedback className="text-alert">
                      {errors.userPhotoFile}
                    </FormFeedback>
                  )}
                </Col>
              </FormGroup>
              <FormGroup row>
                <Label sm={3}>ID photo</Label>
                <Col sm={9}>
                  <UploadButton
                    onSelectFile={idPhotoFile => {
                      setErrors({ ...errors, idPhotoFile: undefined });
                      this.onSelectUserID(idPhotoFile);
                    }}
                    accept="image/png,image/jpeg"
                    maxFileSize={MAX_FILE_SIZE_IN_BYTES}
                  />
                  {errors.idPhotoFile && (
                    <FormFeedback className="text-alert">
                      {errors.idPhotoFile}
                    </FormFeedback>
                  )}
                </Col>
              </FormGroup>
              <FormGroup row>
                <Label sm={3}>Document ID</Label>
                <Col sm={9}>
                  <Input
                    type="text"
                    name="documentId"
                    placeholder="Enter user's document id"
                    invalid={Boolean(errors.documentId)}
                    value={values.documentId}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  {errors.documentId && (
                    <FormFeedback className="text-alert">
                      {errors.documentId}
                    </FormFeedback>
                  )}
                </Col>
              </FormGroup>
              <FormGroup row>
                <Label sm={3}>Date of birth</Label>
                <Col sm={9}>
                  <DatePicker
                    selected={
                      this.state.birthday ||
                      new Date(
                        dateFormatter(birthday, {
                          showTime: false,
                          dateFormat: "array",
                          withoutUnknown: true,
                        }),
                      )
                    }
                    className="form-control"
                    wrapperClassName={datePickerWrapper}
                    onChange={this.onSelectDate}
                  />
                  {errors.dateOfBirth && (
                    <FormFeedback className="text-alert">
                      {errors.dateOfBirth}
                    </FormFeedback>
                  )}
                </Col>
              </FormGroup>
              <FormGroup row>
                <Label sm={3}>Nationality</Label>
                <Col sm={9}>
                  <Input
                    type="text"
                    name="nationality"
                    placeholder="Enter user's nationality"
                    invalid={Boolean(errors.nationality)}
                    value={values.nationality}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  {errors.nationality && (
                    <FormFeedback className="text-alert">
                      {errors.nationality}
                    </FormFeedback>
                  )}
                </Col>
              </FormGroup>
              <FormGroup row>
                <Label sm={3}>Email</Label>
                <Col sm={9}>
                  <Input
                    type="text"
                    name="email"
                    placeholder="Enter user's email"
                    invalid={Boolean(errors.email)}
                    value={values.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  {errors.email && (
                    <FormFeedback className="text-alert">
                      {errors.email}
                    </FormFeedback>
                  )}
                </Col>
              </FormGroup>
              <FormGroup row>
                <Label sm={3}>Home address</Label>
                <Col sm={9}>
                  <Input
                    type="text"
                    name="address"
                    placeholder="Enter user's address"
                    invalid={Boolean(errors.address)}
                    value={values.address}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  {errors.address && (
                    <FormFeedback className="text-alert">
                      {errors.address}
                    </FormFeedback>
                  )}
                </Col>
              </FormGroup>
              <FormGroup row>
                <Label sm={3}>Gender</Label>
                <Col sm={9}>
                  <Input
                    type="select"
                    name="sex"
                    placeholder="Enter user's gender"
                    invalid={Boolean(errors.sex)}
                    value={values.sex}
                    onChange={handleChange}
                    onBlur={handleBlur}>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                  </Input>
                  {errors.sex && (
                    <FormFeedback className="text-alert">
                      {errors.sex}
                    </FormFeedback>
                  )}
                </Col>
              </FormGroup>
            </CardBody>
            <CardFooter>
              <div className="buttons">
                <Button color="primary" type="submit">
                  <span>Update</span>
                </Button>
              </div>
            </CardFooter>
          </Form>
        )}
      </Formik>
    );

    return (
      <Modal
        isOpen={this.props.isOpen}
        onRequestClose={this.beforeClose}
        className={modalStyles}
        overlayClassName={overlayStyles}
        shouldCloseOnOverlayClick={false}
        shouldCloseOnEsc={false}>
        <Card>
          <CardHeader>
            <Button close onClick={this.beforeClose} />

            <span>Update account {this.props.data.accountId}</span>
          </CardHeader>
          {form}
        </Card>
      </Modal>
    );
  }
}

UpdateAccountModal.defaultProps = {
  isOpen: false,
  onModalClose: () => {},
  data: {},
};

UpdateAccountModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onModalClose: PropTypes.func.isRequired,
  data: PropTypes.object.isRequired,
};

export default UpdateAccountModal;
